<template>
  <div></div>
</template>

<script>
export default {
  data() {
    return {};
  },
  computed: {
    getAPIUrl() {
      const base = this.$axios.defaults.baseURL;
      return base.split("/api")[0];
    },
    currentUser() {
      return this.$store.state.auth.user;
    },
    trackingMode() {
      return this.$store.state.auth.settings["hubspot-tracking-mode"];
    }
  },
  async mounted() {
    const loader = this.$loading.show({
      canCancel: true,
      color: "#0367a9",
    });

    let resource_id, resource_name, redirect_url;
    let semanticFlow = false;

    const semanticUrl = this.$route.params.semanticUrl;
    if (semanticUrl) {
      semanticFlow = true;
      try {
        const { data } = await this.$axios.get(`resource-by-slug/${semanticUrl}`);
        resource_id   = data.id;
        resource_name = data.name;
        redirect_url  = data.redirect_url;
      } catch (e) {
        loader.hide();
        window.location.href = "/";
        return;
      }
    } else {
      ({ resource_id, resource_name, redirect_url } = this.$route.query);
    }

    if (!redirect_url) {
      loader.hide();
      window.location.href = "/";
      return;
    }

    // Validate the destination BEFORE running any tracking side-effects. The
    // two entry paths have very different trust models:
    //   /doc/:semanticUrl    → redirect_url comes from GET /resource-by-slug
    //                          (admin-managed via the /edit-resource-json
    //                          route locked down in security/0.5). Trust the
    //                          host but hard-block dangerous URL schemes
    //                          (javascript:/data:/file:/vbscript:/blob:)
    //                          that would execute in this origin's context.
    //                          Preserves legitimate external destinations
    //                          such as YouTube-hosted video resources.
    //   /resource-redirect?redirect_url=…
    //                        → redirect_url comes from an unauthenticated
    //                          query string. Fully attacker-controllable and
    //                          the primary phishing / XSS vector. Requires a
    //                          strict Tarana-host allowlist.
    // Both helpers return null on rejection; we then send the user to "/".
    const safeUrl = semanticFlow
      ? this.safeExternalUrl(redirect_url)
      : this.safeAllowlistedUrl(redirect_url);

    if (!safeUrl) {
      loader.hide();
      window.location.href = "/";
      return;
    }

    if (resource_name) {
      document.title = resource_name;
    }

    const resource = {
      id: resource_id,
      name: resource_name,
      resource_link: redirect_url,
    };

    try {
      await this.logAction(resource);

      const platform = process.env.VUE_APP_PLATFORM;
      if (platform === "production") {
        await this.trackHubspot(resource).catch(() => {});
      }
    } finally {
      loader.hide();
      window.location.href = safeUrl;
    }
  },
  methods: {
    // Redirect-guard helpers (security/1.4). Both accept the raw
    // redirect_url as it arrived (query string or API response), apply a
    // single decodeURIComponent to preserve the pre-fix behavior for links
    // that were double-encoded by the caller, and return either a safe
    // absolute URL string or null.

    // Strict allowlist for the attacker-controllable query-string path.
    // Only Tarana-owned hosts over http/https are accepted.
    safeAllowlistedUrl(redirect_url) {
      const ALLOWED_HOSTS = [
        "taranawireless.com",
        "www.taranawireless.com",
        "support.taranawireless.com",
        "resourcesapi.taranawireless.com",
      ];
      try {
        const u = new URL(decodeURIComponent(redirect_url), window.location.origin);
        if (u.protocol !== "http:" && u.protocol !== "https:") return null;
        if (!ALLOWED_HOSTS.includes(u.hostname)) return null;
        return u.toString();
      } catch (e) {
        return null;
      }
    },

    // Scheme-only guard for the server-fed /doc/:semanticUrl path. The
    // host is admin-controlled; the only thing worth blocking here is
    // dangerous URL schemes that would execute in this origin's context.
    safeExternalUrl(redirect_url) {
      try {
        const u = new URL(decodeURIComponent(redirect_url), window.location.origin);
        if (u.protocol !== "http:" && u.protocol !== "https:") return null;
        return u.toString();
      } catch (e) {
        return null;
      }
    },

    async logAction(resource) {
      if (!resource.id) return;
      const paddedNumber = resource.id.toString().padStart(4, "0");
      const actionJson = {
        pagelink: `lib${paddedNumber}: ${resource.name}`,
        action: "downloaded",
        contentClass: "libe entry",
      };

      if (this.currentUser) {
        actionJson.userId = this.currentUser.user.id;
        actionJson.email = this.currentUser.user.email;
        actionJson.sessionId = this.currentUser.sessionId;
      } else {
        const { user_email } = this.getTrackingParams();
        if (user_email) actionJson.email = user_email;
      }

      try {
        await this.$axios.post("action-update", actionJson);
      } catch (e) {}
    },

    getHubspotUTK() {
      const cookies = document.cookie.split(";");
      for (const cookie of cookies) {
        const [key, value] = cookie.trim().split("=");
        if (key === "hubspotutk") return value;
      }
      return null;
    },

    getTrackingParams() {
      // First check current route query (appended by source page for /doc/ links)
      const query = this.$route.query;
      if (query.user_email) {
        return {
          user_email: query.user_email ? decodeURIComponent(query.user_email) : null,
        };
      }
      // Fallback: try referrer URL
      try {
        if (!document.referrer) return {};
        const url = new URL(document.referrer);
        return {
          user_email: url.searchParams.get("user_email")
            ? decodeURIComponent(url.searchParams.get("user_email"))
            : null,
        };
      } catch (e) {
        return {};
      }
    },

    getResourceLink(resource) {
      if (resource.resource_link) {
        return resource.resource_link;
      } else if (resource.semantic_url) {
        return `${this.getAPIUrl}/doc/${resource.semantic_url}`;
      } else {
        return `${this.getAPIUrl}/storage/${resource.file_path}`;
      }
    },

    async trackHubspot(resource) {
      // Identify visitor then fire behavioural event
      window._hsq = window._hsq || [];

      const identifyPayload = {};
      const referrer = this.getTrackingParams();
      const utk = this.getHubspotUTK();
      const userEmail = referrer.user_email || this.currentUser?.user?.email;
      if (userEmail) {
        identifyPayload.email = userEmail;
      }
      if (utk) {
        identifyPayload.hubspotutk = utk;
      }
      
      if (Object.keys(identifyPayload).length) {
        window._hsq.push(["identify", identifyPayload]);
      }

      const eventName =
        this.trackingMode === "live"
          ? "pe20495833_view_resource_v3"
          : "pe20495833_viewed_resource___test___mode";

      window._hsq.push([
        "trackCustomBehavioralEvent",
        {
          name: eventName,
          properties: {
            resource_id: resource.id,
            resource_name: resource.name,
          },
        },
      ]);

      await this.sendHubspotRequest(resource);
    },

    async sendHubspotRequest(resource) {
      const objectType =
        this.trackingMode === "live" ? "2-38350111" : "2-38634177";

      const HS_API = process.env.VUE_APP_HUBSPOT_API_BASE;
      const endpoints = {
        contactSearch:            `${HS_API}/objects/contacts/search`,
        contactCreate:            `${HS_API}/objects/contacts`,
        customObjectCreate:       `${HS_API}/objects/${objectType}`,
        associateObjectToContact: `${HS_API}/associations/${objectType}/contacts/batch/create`,
      };

      const createCustomObject = (contactEmail, contactId) => {
        const payload = {
          post_url: endpoints.customObjectCreate,
          json_data: {
            properties: {
              resource_id: resource.id,
              resource_name: resource.name,
              contact_email: contactEmail || "Unknown User",
            },
          },
        };
        if (this.currentUser) {
          payload.session_id = this.currentUser.sessionId;
        }
        return this.$axios.post("hubspot-request", payload);
      };

      const buildContactProps = (email) => ({ email });

      const createContact = async (email) =>
        this.$axios.post("hubspot-request", {
          post_url:  endpoints.contactCreate,
          json_data: { properties: await buildContactProps(email) },
        });

      const searchContactByEmail = (email) =>
        this.$axios.post("hubspot-request", {
          post_url: endpoints.contactSearch,
          json_data: {
            filterGroups: [
              {
                filters: [
                  { propertyName: "email", operator: "EQ", value: email },
                ],
              },
            ],
          },
        });

      try {
        let contactId = null;
        const referrer = this.getTrackingParams();
        let contactEmail = referrer.user_email || this.currentUser?.user?.email || "Unknown User";

        if (contactEmail !== "Unknown User") {
          const searchResp = await searchContactByEmail(contactEmail);
          if (searchResp?.data?.results?.length) {
            contactId = searchResp.data.results[0].id;
          } else {
            const newContact = await createContact(contactEmail);
            contactId = newContact?.data?.id;
          }
        }

        const objResp = await createCustomObject(contactEmail, contactId);
        const customObjectId = objResp?.data?.id;

        if (customObjectId && contactId) {
          const assocType =
            this.trackingMode === "live"
              ? "contact_to_viewed_resources"
              : "contact_to_viewed_resource___test___mode";

          await this.$axios.post("hubspot-request", {
            post_url: endpoints.associateObjectToContact,
            json_data: {
              inputs: [
                {
                  from: { id: customObjectId },
                  to: { id: contactId },
                  type: assocType,
                },
              ],
            },
          });
        }
      } catch (e) {}
    },
  },
};
</script>